// SharedPyScriptProvider (RFC-005 B3): an EngineProvider for the embedded Transon visual editor that
// REUSES the docs-site's existing PyScript interpreter — it never loads a second Pyodide. script.py
// exposes the engine glue on `window` (window.transon_validate/transform/version, via create_proxy);
// this proxies to them over JSON strings, exactly like transon-blockly's reference Pyodide host but
// pointed at the shared runtime. dispose() is a no-op: the interpreter is owned by the docs page.

import type {
  EditorDocs,
  EngineProvider,
  ExecutionResult,
  ExampleCase,
  Json,
  ValidationResult,
} from '@transon/editor-react';
import { buildExampleCorpusFromDocs } from '@transon/editor-react';
import { IDocsData } from './types';

type PyCallable = (...args: unknown[]) => unknown;

interface GlueGlobals {
  transon_validate?: PyCallable;
  transon_transform?: PyCallable;
  transon_version?: PyCallable;
  transon_editor_metadata?: PyCallable;
}

function glue(): GlueGlobals {
  return window as unknown as GlueGlobals;
}

/** The glue is present once script.py has run (installed transon + defined the functions). */
function glueReady(): boolean {
  return typeof glue().transon_transform === 'function';
}

/**
 * Create the shared-interpreter EngineProvider. `init()` resolves once the PyScript glue is on
 * `window`; by the time a reader opens the editor the docs page has long since finished loading, but
 * we still poll defensively so an early open waits for readiness rather than failing.
 */
export function createSharedPyScriptEngine(): EngineProvider {
  let status: EngineProvider['status'] = 'idle';

  const require = (name: keyof GlueGlobals): PyCallable => {
    const fn = glue()[name];
    if (typeof fn !== 'function') {
      throw new Error(`SharedPyScriptProvider: ${name} not available (status: ${status})`);
    }
    return fn;
  };

  return {
    get status() {
      return status;
    },
    async init(): Promise<void> {
      if (glueReady()) {
        status = 'ready';
        return;
      }
      status = 'loading';
      await new Promise<void>((resolve) => {
        const timer = setInterval(() => {
          if (glueReady()) {
            clearInterval(timer);
            status = 'ready';
            resolve();
          }
        }, 100);
      });
    },
    async validate(template: Json, o: { marker: string }): Promise<ValidationResult> {
      const out = require('transon_validate')(JSON.stringify(template), o.marker) as string;
      return JSON.parse(out) as ValidationResult;
    },
    async transform(template, input, o): Promise<ExecutionResult> {
      const jsLoader = o.includeLoader
        ? (name: string): string | null => {
            const t = o.includeLoader!(name);
            return t === undefined ? null : JSON.stringify(t);
          }
        : null;
      // OMIT max_include_depth when unset (a JS null reaches Python as a proxy, not None; the
      // Python default must come from the missing argument) — same trap the reference host documents.
      const depthArgs = o.maxIncludeDepth != null ? [o.maxIncludeDepth] : [];
      const out = require('transon_transform')(
        JSON.stringify(template),
        JSON.stringify(input ?? null),
        o.marker,
        JSON.stringify(o.includes ?? {}),
        jsLoader,
        ...depthArgs,
      ) as string;
      const parsed = JSON.parse(out) as ExecutionResult & { files_written?: Record<string, Json> };
      // Python glue emits snake_case `files_written`; the port uses camelCase `filesWritten`.
      const { files_written, ...rest } = parsed;
      return { ...rest, filesWritten: files_written } as ExecutionResult;
    },
    async version(): Promise<{ engine: string; metadata: string }> {
      return JSON.parse(require('transon_version')() as string) as {
        engine: string;
        metadata: string;
      };
    },
    async getEditorMetadata(): Promise<Json> {
      // RFC-007/FR-139 (metadata-contract §3): proxy the engine's get_editor_metadata() verbatim.
      // A missing glue function (stale cached script.py) or a Python-side error rejects here,
      // which is exactly the editor's FR-140 fallback path: snapshot surface + diagnostic.
      const out = JSON.parse(require('transon_editor_metadata')() as string) as {
        status: string;
        metadata?: Json;
        error_message?: string;
      };
      if (out.status !== 'ok') {
        throw new Error(out.error_message ?? 'engine editor-metadata export failed');
      }
      return out.metadata as Json;
    },
    dispose(): void {
      // No-op: the interpreter is the docs page's shared PyScript runtime, never torn down here.
    },
  };
}

/**
 * Derive the embedded editor's example corpus from the engine `get_all_docs()` payload via the
 * editor's own `buildExampleCorpusFromDocs` (FR-132, editor-react 0.2.1). This keeps the
 * `rule`/`tier` joins the tiered picker groups by — the previous hand-mapping dropped them, so
 * the embed rendered every case under one "Reference · other" group with colliding labels. Only
 * the rule-entry shape is unwrapped (`{rule:{name}, examples, params:[{param:{name}, examples}]}`
 * → `{name, examples, params:[{name, examples}]}`); the reference lists themselves stay verbatim
 * — the engine already folds operator/function example refs into `expr`/`call`, so the wire refs
 * match the editor-metadata refs exactly.
 */
export function toExampleCases(docs: IDocsData): ExampleCase[] {
  const editorDocs = {
    examples: docs.examples,
    worked_examples: docs.worked_examples ?? [],
    recipes: docs.recipes ?? [],
    rules: docs.rules.map((r) => ({
      name: r.rule.name,
      examples: r.examples,
      params: (r.params ?? []).map((p) => ({ name: p.param.name, examples: p.examples })),
    })),
    operators: [],
    functions: [],
  } as unknown as EditorDocs;
  return buildExampleCorpusFromDocs(editorDocs);
}
